import { assertNever } from '@gitbook/util-ts';

import { genID } from '../helpers';
import type { FileLocation, FilesLocationGroup, NativeFile } from './types';

/*
 * Different kind of files can be saved in the storage buckets.
 * To handle them, we structure paths with the following convention:
 *
 *   /{prefix}/{uniqueId}/{fileName}
 *
 * The {prefix} is derived from the FileLocation argument.
 *
 * To avoid any conflict on direct upload, we append {uniqueId} to the path.
 * This ID is unrelated to the file keys in a revision.
 * This ID can be set to a specific value by passing an "id" to the FileLocation.
 *
 * The {fileName} is then only use a nice to read filename when the user download the file.
 *
 * Different buckets can be derivated from the FileLocation (not all files are stored in the bucket, ex: PDF export).
 * This logic is abstracted in files.ts and should not impact end-implementation.
 * When using the storage module, you don't need to think about which buckets hold the files.
 */

/**
 * Get the path for a location prefix.
 */
export function getFilesLocationGroupPrefix(location: FilesLocationGroup): string {
    switch (location.kind) {
        case 'space':
            return `spaces/${location.space}`;
        case 'user':
            return `users/${location.user}`;
        case 'organization':
            return `organizations/${location.organization}`;
        case 'integration':
            return `integrations/${location.integration}`;
        case 'site':
            return `organizations/${location.organization}/sites/${location.site}`;
        default:
            assertNever(location);
    }
}

/**
 * Get the base path for a location.
 */
export function getFileLocationPrefix(location: FileLocation): string {
    switch (location.kind) {
        case 'space_file':
            return `spaces/${location.space}/uploads`;
        case 'space_import':
            return `spaces/${location.space}/imports`;
        case 'space_icon':
            return `spaces/${location.space}/icon`;
        case 'page_cover':
            return `spaces/${location.space}/pages/${location.page}/cover`;
        case 'collection_icon':
            return `collections/${location.collection}/icon`;
        case 'org_logo':
            return `organizations/${location.organization}/logo`;
        case 'user_avatar':
            return `users/${location.user}/avatars`;
        case 'integration_icon':
            return `integrations/${location.integration}/icon`;
        case 'integration_image':
            return `integrations/${location.integration}/image`;
        case 'site_icon':
            return `organizations/${location.organization}/sites/${location.site}/icon`;
        case 'site_logo':
            return `organizations/${location.organization}/sites/${location.site}/logo`;
        case 'site_socialpreview':
            return `organizations/${location.organization}/sites/${location.site}/socialpreview`;
        default:
            assertNever(location);
    }
}

/**
 * Get a path for a file in a location.
 */
export function getFilePath(location: FileLocation, file: NativeFile | string): string {
    const name = typeof file === 'string' ? file : file.name;
    const uniqueFileId = 'id' in location ? location.id : genID();

    return `${getFileLocationPrefix(location)}/${uniqueFileId}/${name}`;
}
